/**
 * Teams Landing Page
 * This is the page displayed when users click on Teams notifications or open the app in Teams
 * 
 * CRITICAL: This integrates with Microsoft Teams and cannot be tested until deployed to production
 * URL must match Teams app manifest configuration
 */

import React, { useState, useEffect } from 'react';
import { app } from '@microsoft/teams-js';
import {
  Stack,
  Text,
  PrimaryButton,
  Icon,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType
} from '@fluentui/react';
import { useConnectWiseLinks } from '@/connectwise';

interface TeamsLandingPageProps {
  /** If true, shows the notification alert view */
  fromNotification?: boolean;
}

const TeamsLandingPage: React.FC<TeamsLandingPageProps> = ({ fromNotification: fromNotificationProp }) => {
  const [isTeamsInitialized, setIsTeamsInitialized] = useState(false);
  const [isFromNotification, setIsFromNotification] = useState(fromNotificationProp || false);
  const [teamsContext, setTeamsContext] = useState<app.Context | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { loginUrl } = useConnectWiseLinks();

  useEffect(() => {
    // Initialize Teams SDK
    const initTeams = async () => {
      try {
        await app.initialize();
        
        // Get context to understand the Teams environment
        const context = await app.getContext();
        setTeamsContext(context);
        setIsTeamsInitialized(true);

        // Check URL parameters to see if user came from notification
        const urlParams = new URLSearchParams(window.location.search);
        const fromNotificationParam = urlParams.get('fromNotification');
        
        if (fromNotificationParam === 'true') {
          setIsFromNotification(true);
        }

        // Notify Teams that app is ready
        app.notifySuccess();
      } catch (err) {
        console.error('Failed to initialize Teams SDK:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize Teams');
      }
    };

    initTeams();
  }, []);

  const handleOpenConnectWise = () => {
    if (!loginUrl) return;
    window.open(loginUrl, '_blank', 'noopener,noreferrer');
  };

  if (!isTeamsInitialized) {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        tokens={{ padding: 40 }}
        styles={{ root: { height: '100vh', backgroundColor: '#f3f2f1' } }}
      >
        <Spinner size={SpinnerSize.large} label="Initializing Teams app..." />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack
        tokens={{ padding: 20, childrenGap: 16 }}
        styles={{ root: { height: '100vh', backgroundColor: '#f3f2f1' } }}
      >
        <MessageBar messageBarType={MessageBarType.error}>
          Failed to initialize Teams: {error}
        </MessageBar>
      </Stack>
    );
  }

  // Notification Alert View
  if (isFromNotification) {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        tokens={{ padding: 40, childrenGap: 24 }}
        styles={{
          root: {
            height: '100vh',
            backgroundColor: '#fef0f1',
            borderTop: '4px solid #d13438'
          }
        }}
      >
        <Stack
          horizontalAlign="center"
          tokens={{ childrenGap: 20 }}
          styles={{
            root: {
              maxWidth: 600,
              padding: 40,
              backgroundColor: 'white',
              borderRadius: 8,
              boxShadow: '0 3.2px 7.2px 0 rgba(0,0,0,0.132), 0 0.6px 1.8px 0 rgba(0,0,0,0.108)'
            }
          }}
        >
          <Icon
            iconName="WarningSolid"
            styles={{
              root: {
                fontSize: 64,
                color: '#d13438',
                animation: 'pulse 2s ease-in-out infinite'
              }
            }}
          />

          <Text
            variant="xxLarge"
            styles={{
              root: {
                fontWeight: 700,
                color: '#d13438',
                textAlign: 'center'
              }
            }}
          >
            Immediate Attention Required
          </Text>

          <Text
            variant="large"
            styles={{
              root: {
                textAlign: 'center',
                color: '#323130',
                lineHeight: 1.6
              }
            }}
          >
            A ticket requires your immediate attention and has been unassigned for 20+ minutes.
          </Text>

          <Text
            variant="medium"
            styles={{
              root: {
                textAlign: 'center',
                color: '#605e5c'
              }
            }}
          >
            Please check your <strong>Service Board</strong> in ConnectWise to review and assign pending tickets.
          </Text>

          <PrimaryButton
            text="Open ConnectWise Service Board"
            iconProps={{ iconName: 'OpenInNewWindow' }}
            onClick={handleOpenConnectWise}
            disabled={!loginUrl}
            styles={{
              root: {
                marginTop: 16,
                padding: '12px 32px',
                height: 'auto'
              }
            }}
          />
        </Stack>

        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.6; }
            }
          `}
        </style>
      </Stack>
    );
  }

  // Dashboard View
  return (
    <Stack
      tokens={{ padding: 40, childrenGap: 32 }}
      styles={{
        root: {
          minHeight: '100vh',
          backgroundColor: teamsContext?.app.theme === 'dark' ? '#2d2c2c' : '#f3f2f1'
        }
      }}
    >
      <Stack
        tokens={{ childrenGap: 16 }}
        styles={{
          root: {
            maxWidth: 800,
            margin: '0 auto',
            padding: 32,
            backgroundColor: teamsContext?.app.theme === 'dark' ? '#3b3a39' : 'white',
            borderRadius: 8,
            boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.132), 0 0.3px 0.9px 0 rgba(0,0,0,0.108)'
          }
        }}
      >
        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
          <Icon
            iconName="Ringer"
            styles={{
              root: {
                fontSize: 48,
                color: '#0078d4'
              }
            }}
          />
          <Stack>
            <Text
              variant="xxLarge"
              styles={{
                root: {
                  fontWeight: 700,
                  color: teamsContext?.app.theme === 'dark' ? '#ffffff' : '#323130'
                }
              }}
            >
              On-Call Alert System
            </Text>
            <Text
              variant="large"
              styles={{
                root: {
                  color: teamsContext?.app.theme === 'dark' ? '#c8c6c4' : '#605e5c'
                }
              }}
            >
              Automated notification system for timely ticket response
            </Text>
          </Stack>
        </Stack>

        <Stack tokens={{ childrenGap: 16, padding: '16px 0' }}>
          <Text
            variant="xLarge"
            styles={{
              root: {
                fontWeight: 600,
                color: teamsContext?.app.theme === 'dark' ? '#ffffff' : '#323130'
              }
            }}
          >
            What is this application?
          </Text>
          <Text
            variant="medium"
            styles={{
              root: {
                lineHeight: 1.6,
                color: teamsContext?.app.theme === 'dark' ? '#c8c6c4' : '#605e5c'
              }
            }}
          >
            This system monitors ConnectWise tickets and sends on-call notifications through Microsoft Teams
            if tickets haven't been responded to within the configured initial delay period. The notifications are sent to the currently
            configured on-call member and any always-send recipients.
          </Text>

          <Text
            variant="medium"
            styles={{
              root: {
                lineHeight: 1.6,
                color: teamsContext?.app.theme === 'dark' ? '#c8c6c4' : '#605e5c'
              }
            }}
          >
            When you receive a notification, click on it to be directed here with instructions to check
            your Service Board in ConnectWise.
          </Text>
        </Stack>

        <PrimaryButton
          text="Open ConnectWise Service Board"
          iconProps={{ iconName: 'OpenInNewWindow' }}
          onClick={handleOpenConnectWise}
          disabled={!loginUrl}
          styles={{
            root: {
              marginTop: 16,
              alignSelf: 'flex-start'
            }
          }}
        />
      </Stack>

      {teamsContext && (
        <Stack
          styles={{
            root: {
              maxWidth: 800,
              margin: '0 auto',
              padding: 16,
              fontSize: 11,
              color: '#a19f9d',
              fontFamily: 'monospace'
            }
          }}
        >
          <Text variant="small">
            Teams Theme: {teamsContext.app.theme || 'default'} | 
            User ID: {teamsContext.user?.id || 'unknown'} | 
            Locale: {teamsContext.app.locale || 'en-us'}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

export default TeamsLandingPage;
